<template>
  <div v-if="segmentosLocais">
    <v-row no-gutters>
      <p v-if="error" class="error--text">{{ errorMessage }}</p>
    </v-row>

    <v-row v-if="!disableSegmentView && segmentosLocais && segmentosLocais.length > 0">
      <v-col>
        <v-alert class="ma-0" color="blue" dense text type="info">
          Clique em cima do segmento e uma nova aba será aberta para visualizar os critérios da segmentação.
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-if="campaignCreatedBeforeViewSegmentation && disableSegmentView">
      <v-col>
        <v-alert class="alert-color ma-0" dense icon="mdi-alert-outline">
          A visualização dos critérios de segmentação está disponível apenas para campanhas criadas após {{ viewSegmentationDate }}.
        </v-alert>
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col :cols="segmentosLocais && segmentosLocais.length > 1 ? 10 : 12">
        <v-chip-group :class="applyMarginFix" show-arrows active-class="primary--text" :column="showAllSegments">
          <v-chip
            v-for="(segmento, index) in segmentosLocais"
            :key="index"
            small
            :close="!removeSegmentDisabled"
            :ripple="false"
            @click="goToSegmentation(index)"
            @click:close="remove(index)"
          >
            <span>
              <strong class="pr-2">{{ segmento.name }}</strong>
              <v-progress-circular v-if="segmento.updating" indeterminate color="primary" size="15" />
              <span v-else>({{ segmento.clients_qty | zeroWhenEmpty | localeString }} clientes)</span>
            </span>
          </v-chip>
        </v-chip-group>
      </v-col>
      <v-col v-if="segmentosLocais && segmentosLocais.length > 1" cols="auto" align-self="center">
        <span class="link" @click="showAllSegments = !showAllSegments">{{ showAllSegments ? 'Minimizar' : 'Mostrar tudo' }}</span>
      </v-col>
    </v-row>

    <v-row v-if="segmentosLocais.length > 0 && !isOfferSchedulePersonalized" class="pb-1" no-gutters>
      <v-col cols="auto">
        <span class="texto-corrido">{{ segmentUpdateMessage() }} </span>
      </v-col>

      <v-col v-if="!isIndustryCampaign" cols="auto">
        <span v-if="!isStaticSegment" :class="updating || blockSegmentsUpdate ? 'link disabled ml-2' : 'link ml-2'" @click="updateSegments"
          >Clique aqui para atualizar.</span
        >
        <span v-else class="disabled-link ml-2">Clique aqui para atualizar.</span>
      </v-col>

      <v-col v-if="!isGestaoOfertas" cols="auto">
        <span class="ml-2">
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <mf-icon small>info</mf-icon>
              </span>
            </template>
            <span>Os segmentos também serão<br />atualizados quando as campanhas<br />forem disparadas.</span>
          </v-tooltip>
        </span>
      </v-col>
    </v-row>

    <v-row justify="start">
      <v-col v-if="!isIndustryCampaign" cols="auto">
        <segmentos-dialog
          label="Segmentos"
          :required="false"
          :disable="disable"
          :single-select="singleSelect"
          :selecionados.sync="segmentosLocais"
          :is-campaign.sync="isCampaign"
          :is-offer-schedule-personalized="isOfferSchedulePersonalized"
        />
      </v-col>
      <v-col v-if="hasPiiAccess && isCampaign" cols="auto">
        <export-client-button :segment="segmentosLocais[0]" :disabled="emptySegments" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import SegmentosDialog from '@/modules/campanhas/views/components/SegmentosDialog.vue'
import ExportClientButton from '@/modules/clientes/segmentation/views/components/ExportClientButton.vue'
import { DateTime } from 'luxon'

export default {
  components: {
    SegmentosDialog,
    ExportClientButton,
  },
  filters: {
    zeroWhenEmpty(value) {
      if (!value) return 0
      return value
    },
  },
  props: {
    segmentos: Array,
    error: Boolean,
    disable: Boolean,
    singleSelect: Boolean,
    blockSegmentsUpdate: {
      type: Boolean,
      default: false,
    },
    isStaticSegment: {
      type: Boolean,
      default: false,
    },
    isCampaign: {
      type: Boolean,
      default: false,
    },
    isOfferSchedulePersonalized: {
      type: Boolean,
      default: false,
    },
    isIndustryCampaign: {
      type: Boolean,
      default: false,
    },
    tooltipIndustryMessage: String,
    campaignId: {
      type: String,
      default: '',
    },
    disableSegmentView: {
      type: Boolean,
      default: false,
    },
    disableSegmentViewMessage: {
      type: String,
      default: 'Não é possível visualizar critérios desta campanha',
    },
    campaignCreatedAt: String,
  },
  data() {
    return {
      showAllSegments: false,
      updating: false,
      dateViewSegmentation: '2023-02-01',
    }
  },
  computed: {
    isGestaoOfertas() {
      return this.$route.path.includes('gestao-ofertas')
    },
    segmentosLocais: {
      get() {
        return this.segmentos
      },
      set(value) {
        this.$emit('update:segmentos', value)
        this.$emit('input', value)
      },
    },
    refreshedSegments() {
      return this.$store.getters.refreshedSegments
    },
    segmentsUpdating() {
      return this.$store.getters.updatingSegmentsIds
    },
    errorMessage() {
      if (this.error) return this.$t('campanhas.adicione_segmento_para_criar')
      else return ''
    },
    emptySegments() {
      return !this.segmentosLocais || this.segmentosLocais.length <= 0
    },

    applyMarginFix() {
      if (!this.showAllSegments && this.segmentosLocais && this.segmentosLocais.length > 1) {
        return 'fix-margin'
      }
      return ''
    },

    removeSegmentDisabled() {
      return this.disable || this.isOfferSchedulePersonalized
    },

    campaignCreatedBeforeViewSegmentation() {
      if (this.campaignCreatedAt) {
        return DateTime.fromISO(this.campaignCreatedAt).startOf('day') <= DateTime.fromISO(this.dateViewSegmentation).startOf('day')
      }

      return false
    },

    viewSegmentationDate() {
      return DateTime.fromISO(this.dateViewSegmentation).toFormat('dd/MM/yyyy')
    },
  },
  watch: {
    segmentsUpdating(value) {
      this.updating = value.length > 0
      this.segmentosLocais.forEach((item) => {
        if (value.includes(item._id)) {
          item.updating = true
        } else {
          item.updating = false
        }
      })
    },
    refreshedSegments(refreshedSegments) {
      this.segmentosLocais.forEach((item) => {
        const index = refreshedSegments.findIndex((refreshedSegment) => refreshedSegment._id === item._id)
        if (index > -1) {
          item.clients_refreshed_at = refreshedSegments[index].clients_refreshed_at
          item.clients_qty = refreshedSegments[index].clients_qty
          item.updating = false
        }
      })
      this.updating = false
    },
  },
  methods: {
    remove(index) {
      if (this.removeSegmentDisabled) return
      this.segmentosLocais.splice(index, 1)
    },
    segmentUpdateMessage() {
      let upToDate = true
      this.segmentosLocais.forEach((segmento) => {
        if (DateTime.fromISO(segmento.clients_refreshed_at).toFormat('dd') < DateTime.now().toFormat('dd')) {
          upToDate = false
        }
      })
      if (this.segmentosLocais.length === 1) {
        return `O segmento selecionado está ${upToDate ? 'atualizado' : 'desatualizado'}.`
      }
      return `Os segmentos selecionados estão ${upToDate ? 'atualizados' : 'desatualizados'}.`
    },
    updateSegments() {
      if (this.blockSegmentsUpdate) return
      if (this.updating) return
      this.$store
        .dispatch(
          'updateSegments',
          this.segmentosLocais.map((segment) => segment._id)
        )
        .catch((error) => {
          console.log(error)
          this.$snackbar({
            message: 'Não foi possível atualizar o segmento, tente novamente mais tarde ou entre em contato com o suporte',
            snackbarColor: 'error',
          })
          this.updating = false
        })
    },

    goToSegmentation(index) {
      if (this.disableSegmentView) {
        this.$snackbar({ message: this.disableSegmentViewMessage, snackbarColor: 'error' })
        return
      }

      const segment = this.segmentosLocais[index]

      if (!segment) return

      const routeData = this.$router.resolve({
        name: 'visualizar-segmentacao',
        params: {
          campaignId: this.campaignId,
          segmentId: segment._id,
        },
      })
      window.open(routeData.href, '_blank')
    },
  },
}
</script>
<style lang="scss" scoped>
.disabled {
  color: $cinza-0;
}
.disabled-link {
  color: $cinza-d10;
  font-size: 14px;
  font-weight: $font-weight-regular;
  line-height: $line-height-text;
  letter-spacing: $letterspacing-1;
  text-decoration: underline;
}

.fix-margin {
  margin-left: -20px;
}

.alert-color {
  background-color: #fff9e3;
}
</style>
